import { FC, useState } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router";

import { CardChartWithCalendarProps } from "./types";
import Button from "../Button/Button";
import Chart from "../Chart/Chart";
import {
  ArrawUpIcon,
  ArrawDownIcon,
  HourglassIcon,
  UnwatchIcon,
  EyeIcon,
  TrophyIcon,
  ArrawRightIcon
} from "../icons";
import Accordion from "../Accordion";
import Calendar from "../Calendar";
import ValueWithDifference from "../ValueWithDifference/ValueWithDifference";
import Badge from "../Badge";
import { ERoutes } from "../../constants/routes";
import { ChartSeriesKey, VisibleSeries } from "../Chart/types";
import { getSentimentIcon } from "../../utils/getSentimentIcon";
import Skeleton from "../Skeleton/Skeleton";
import { ScoreWeekly } from "../../store/scores/types";
import BrandLogo from "../BrandLogo/BrandLogo";
import { UpgradePlanButton } from "../UpgradePlanButton";

import styles from "./CardChartWithCalendar.module.css";



const CardChartWithCalendar: FC<CardChartWithCalendarProps> = ({
  item,
  type,
  icon: Icon,
  onHideItem,
  onDotClick,
  hideItemTitle,
  isActiveChart = false,
  defaultIsOpen = false,
  htmlId,
  linkFormat,
  currentBrandId,
  customTitleClassName,
  dailyAverageLoading,
  dailyAverageScores,
  rangeValues,
  weeklyScores,
  weeklyScoresLoading,
  isProLocked,
  onUpgrade,
}) => {
  const navigate = useNavigate();
  const [isChart, setIsChart] = useState(isActiveChart);

  const [visibleSeries, setVisibleSeries] = useState<VisibleSeries>({
    [ChartSeriesKey.Visibility]: true,
    [ChartSeriesKey.Sentiment]: true,
    [ChartSeriesKey.Ranking]: true,
  });

  const logoUrl = type === 'brand' && 'logoUrl' in item && item.logoUrl ? item.logoUrl : null;
  const name = 'title' in item ? item.title : item.name;

  const handleLegendToggle = (key: ChartSeriesKey) => {
    setVisibleSeries(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleRemoveItem = () => {
    onHideItem?.(String(item.id), false);
  }

  const handleDotClick = (created_at: string) => {
    onDotClick(created_at);
  }

  const renderScore = (isOpen: boolean, setIsOpen: (isOpen: boolean) => void) => {
    if (isProLocked) {
      return (
        <UpgradePlanButton
          className={styles["score__empty"]}
          onUpgrade={onUpgrade}
        />
      );
    }

    if (
      dailyAverageLoading === "success" && 
      !dailyAverageScores && 
      !weeklyScores
    ) {
      return (
        <div className={styles["score__empty"]}>
          <HourglassIcon /> <p>calculating score</p>
        </div>
      )
    }

    const sentimentValue = weeklyScores?.['current_week_avg_sentiment' as keyof ScoreWeekly] as number | null | undefined;
    const SentimentIcon = getSentimentIcon(sentimentValue);

    const scoreButtons = [
      {
        key: ChartSeriesKey.Visibility,
        icon: <EyeIcon />,
        className: styles["is-visibility"],
        valueKey: 'current_week_avg_visibility',
        differenceKey: 'week_difference_visibility',
      },
      {
        key: ChartSeriesKey.Sentiment,
        icon: <SentimentIcon />,
        className: styles["is-sentiment"],
        valueKey: 'current_week_avg_sentiment',
        differenceKey: 'week_difference_sentiment',
      },
      {
        key: ChartSeriesKey.Ranking,
        icon: <TrophyIcon />,
        className: styles["is-ranking"],
        valueKey: 'current_week_avg_position',
        differenceKey: 'week_difference_position',
      },
    ];

    return (
      <>
        {scoreButtons.map(({ key, icon, className, valueKey, differenceKey }) => (
          <button
            key={key}
            type="button"
            className={classNames(
              styles["score__button"],
              className,
              { [styles["is-disabled"]]: !visibleSeries[key] },
              { [styles["is-active"]]: visibleSeries[key] && isOpen }
            )}
            onClick={() => {
              handleLegendToggle(key);
              setIsOpen(true);
            }}
          >
            <ValueWithDifference
              className={styles["score__value"]}
              icon={icon}
              value={weeklyScores?.[valueKey as keyof ScoreWeekly]}
              difference={weeklyScores?.[differenceKey as keyof ScoreWeekly]}
            />
          </button>
        ))}
      </>
    )
  }

  const renderHeader = (isOpen: boolean, setIsOpen: (isOpen: boolean) => void) => {
    return (
      <div className={styles["card-calendar__container"]}>
        <div className={styles['card-calendar__wrapper']}>
          <div className={styles["card-calendar__left"]} onClick={() => setIsOpen(true)}>
            <Button
              className={styles["card-calendar__icon-accordion"]}
              typeStyle="round"
              Icon={isOpen ? ArrawUpIcon : ArrawDownIcon}
            />
            {logoUrl && (
              <BrandLogo
                logoUrl={logoUrl}
                name={name}
                size="small"
                className={styles["card-calendar__logo"]}
              />
            )}
            {Icon &&
              <div className={styles["card-calendar__icon"]}>
                <Icon />
              </div>
            }
            <h3 className={classNames(styles["card-calendar__title"], customTitleClassName)}>{name}</h3>

            {weeklyScoresLoading === "success" && !weeklyScores &&  <Badge text="new" className={styles["card-calendar__badge"]} />}
          </div>
          <div className={classNames(styles["card-calendar__score"], styles["score"])}>
            { renderScore(isOpen, setIsOpen) }
          </div>
        </div>
      </div>
    )
  }

  const renderSubHeader = (isOpen: boolean) => {
    return (
      isOpen && onHideItem && <Button
        onClick={handleRemoveItem}
        className={styles["card-calendar__remove"]}
        typeStyle="empty"
        Icon={UnwatchIcon}
        label={hideItemTitle} />
    )
  }

  const renderContent = () => {
    if (dailyAverageLoading === "success" && !dailyAverageScores) {
      return (
        <div className={styles["chart__empty"]}>
          <p>No data for the selected period</p>
        </div>
      );
    }

    if (!dailyAverageScores) {
      return (
        <div className={styles["chart__skeleton"]}>
          <Skeleton height={540} />
        </div>
      );
    }

    return (
      <>
        <header className={styles["chart__header"]}>
          <div className={styles['switch']}>
            <Button 
              typeStyle="empty" 
              className={classNames(
                styles['switch__button'], 
                !isChart && styles['is-active'])} 
                label="Chart" 
                onClick={() => setIsChart(false)} />
            <Button 
              typeStyle="empty" 
              className={classNames(
                styles['switch__button'], 
                isChart && styles['is-active'])} 
                label="Calendar" 
                onClick={() => setIsChart(true)} />
          </div>

          {type !== 'prompt' && (<p className={styles["chart__link-prompts"]}>
            <span>Prompts</span>
            <Button typeStyle="round-outline" Icon={ArrawRightIcon} onClick={() => navigate(ERoutes.Prompts.replace(":id", String(currentBrandId)))} />
          </p>)}
        </header>
        <div className={styles["chart__container"]}>
          {!isChart && dailyAverageScores ? (
            <Chart data={dailyAverageScores} rangeValues={rangeValues} onDotClick={handleDotClick} visibleSeries={visibleSeries} />
          ) : (
            <Calendar 
              data={dailyAverageScores || []} 
              linkFormat={linkFormat} 
              currentBrandId={currentBrandId} 
              hashId={String(item.id)} />
          )}
        </div>
      </>
    )
  }


  return (
    <Accordion
      isDisabled={isProLocked}
      defaultIsOpen={defaultIsOpen}
      htmlId={htmlId}
      classNameContainer={styles["card-calendar"]}
      classNameHeader={styles["card-calendar__header"]}
      classNameContent={styles["card-calendar__content"]}
      header={renderHeader}
      subHeader={renderSubHeader}
      content={renderContent} />
  );
};

export default CardChartWithCalendar;
